import { NetworkStatus, useApolloClient } from '@apollo/client';
import { first } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { AI_ONBOARDING_MODAL_VARIANT_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.constants';
import {
  AIOnboardingModalProps,
  AIOnboardingModalVariant,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import {
  getAIOnboardingModalSuggestionQueryOptions,
  isAIOnboardingModalPageLoading,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.utils';
import { AIOnboardingModalFooter } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalFooter';
import { AIOnboardingModalHeader } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalHeader';
import { AIOnboardingModalRightContent } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalRightContent';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import { AIOnboardingModalProvider } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.provider';
import {
  AiOnboardingModal_AiSuggestionFragment,
  AiOnboardingModal_HouseholdFragment,
} from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import {
  useFetchAiOnboardingModalAllSuggestionsV2Query,
  useFetchAiOnboardingModalDataV2Query,
} from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModalV2.generated';
import { useAIOnboardingModalPagination } from '@/modules/aiOnboarding/AIOnboardingModal/hooks/useAIOnboardingModalPagination';
import { useSelectedSuggestionEvidenceContext } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.context';
import { SelectedSuggestionEvidenceProvider } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.provider';
import { getInitialDocumentIdFromSuggestion } from '@/modules/aiSuggestions/kgV2Resolution.utils';
import { DocumentChatPane } from '@/modules/chat/components/DocumentChatPane';
import {
  DocumentChatProvider,
  useOptionalDocumentChatContext,
} from '@/modules/chat/contexts/DocumentChatProvider';
import { SplitScreenLayoutWithDocuments } from '@/modules/documents/components/SplitScreenLayoutWithDocuments/SplitScreenLayoutWithDocuments';
import { orderDocsAlphaByName } from '@/modules/documents/documents.utils';
import { MultiDocumentUploaderProvider } from '@/modules/documents/MultiDocumentUploader/context/MultiDocumentUploader.provider';
import { FullScreenModalHouseholdChatHost } from '@/modules/household/chat/components/FullScreenModalHouseholdChatHost';
import { HouseholdBillableKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

function AIOnboardingModalInner({
  initialDocumentId,
}: {
  initialDocumentId?: string;
}) {
  const { isOpen, onClose, household, variant } = useAIOnboardingModalContext();

  const {
    documentPanelProps: {
      activeDocumentId,
      activeDocumentViewerTab,
      annotations,
      activeAnnotationIndex,
      searchQuery,
      searchLoading,
    },
    setDocumentPanelProps,
  } = useSelectedSuggestionEvidenceContext();

  // Clear the chat when the modal opens
  const chatContext = useOptionalDocumentChatContext();
  useEffect(() => {
    chatContext?.clearChat();

    // Only run when modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FullScreenModal isOpen={isOpen} onClose={onClose}>
      <FullScreenModalHouseholdChatHost>
        {({ householdChatPane, isHouseholdChatReplacementEnabled }) => (
          <SplitScreenLayoutWithDocuments
            householdId={household.id}
            showAllHouseholdDocuments={true}
            header={<AIOnboardingModalHeader />}
            rightContent={<AIOnboardingModalRightContent />}
            rightPane={
              isHouseholdChatReplacementEnabled ? (
                householdChatPane
              ) : (
                <DocumentChatPane
                  documentId={activeDocumentId ?? initialDocumentId}
                />
              )
            }
            footer={<AIOnboardingModalFooter />}
            hideAddDocument={true}
            overrideActiveDocumentId={activeDocumentId ?? initialDocumentId}
            onUpdateActiveDocumentId={({
              documentId,
              shouldClearSearchResults,
            }) => {
              chatContext?.setDocumentId(documentId);
              chatContext?.clearChat();

              if (shouldClearSearchResults) {
                // Clear out the annotations and search-related props
                setDocumentPanelProps((prev) => ({
                  ...prev,
                  activeDocumentId: documentId,
                  searchQuery: '',
                  annotations: undefined,
                  activeAnnotationIndex: undefined,
                  searchLoading: false,
                }));
                return;
              }
              // Otherwise, only update the document id and tab
              setDocumentPanelProps((prev) => ({
                ...prev,
                activeDocumentId: documentId,
              }));
            }}
            overrideDocumentViewerActiveTab={activeDocumentViewerTab}
            onUpdateDocumentViewerActiveTab={() => {
              // Unset the selected suggestion when the user switches between the document/summary views.
              setDocumentPanelProps({
                activeDocumentViewerTab: undefined,
                activeDocumentId: undefined,
                annotations: undefined,
                activeAnnotationIndex: undefined,
                searchQuery: '',
                searchLoading: false,
              });
            }}
            overrideSearchQuery={searchQuery}
            overrideSearchLoading={searchLoading}
            overrideAnnotations={annotations}
            overrideActiveAnnotationIndex={activeAnnotationIndex}
            autoZoom={variant === AIOnboardingModalVariant.HOLDINGS}
            // Order documents alphabetically by name in the viewer's document selector
            documentOrderFn={orderDocsAlphaByName}
          />
        )}
      </FullScreenModalHouseholdChatHost>
    </FullScreenModal>
  );
}

export function AIOnboardingModal(props: AIOnboardingModalProps) {
  const client = useApolloClient();
  const { reportError } = useReportError();
  const { createErrorFeedback } = useFeedback();

  const {
    householdId,
    variant,
    initialSelectedSuggestion,
    suggestionsWhere,
    additionalReferenceSuggestions,
    skipNavigationOnComplete,
    onClose,
  } = props;

  const footerRef = useRef<HTMLDivElement>(null);
  const hasSavedSuggestionRef = useRef(false);
  const savedSuggestionIDsRef = useRef(new Set<string>());
  const initialTotalCountRef = useRef<number | null>(null);
  const isOpenRef = useRef(props.isOpen);
  isOpenRef.current = props.isOpen;
  const { first: pageSize, suggestionsWhere: effectiveSuggestionsWhere } =
    useMemo(
      () =>
        getAIOnboardingModalSuggestionQueryOptions({
          initialSelectedSuggestion,
          suggestionsWhere,
        }),
      // Keep the result set stable while the modal is open so live caller
      // refetches cannot invalidate cursors or discard page-local edits.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh the query scope only between modal sessions
      [props.isOpen]
    );
  const {
    after,
    currentPage,
    hasPreviousPage,
    onNextPage,
    onPreviousPage,
    onSuggestionReviewed,
    reviewedCountBeforeCurrentPage,
    reviewedSuggestionIDs,
  } = useAIOnboardingModalPagination({ isOpen: props.isOpen });

  const refetchDataAfterClose = useCallback(() => {
    setTimeout(() => {
      const { refetchQueries } = AI_ONBOARDING_MODAL_VARIANT_CONFIGS[variant];
      if (refetchQueries?.length) {
        void client.refetchQueries({ include: refetchQueries });
      }

      void client.refetchQueries({
        updateCache(cache) {
          cache.evict({ fieldName: 'aiSuggestions' });
          if (
            variant === AIOnboardingModalVariant.ALL_ENTITIES ||
            variant === AIOnboardingModalVariant.TRUST_ENTITIES ||
            variant === AIOnboardingModalVariant.BUSINESS_ENTITIES ||
            variant === AIOnboardingModalVariant.HOLDINGS
          ) {
            cache.evict({ fieldName: 'entities' });
          }
          if (variant === AIOnboardingModalVariant.HOLDINGS) {
            cache.evict({ fieldName: 'liabilities' });
          }
          if (
            variant === AIOnboardingModalVariant.ALL_ENTITIES ||
            variant === AIOnboardingModalVariant.TRUST_ENTITIES ||
            variant === AIOnboardingModalVariant.BUSINESS_ENTITIES
          ) {
            // Created records can resolve KG V2 principal nodes in other
            // suggestions, so refetch the cached node resolution edges.
            cache.evict({ fieldName: 'fetchHouseholdKGV2Nodes' });
          }
          cache.gc();
        },
      });
    }, 0);
  }, [client, variant]);

  const handleClose = useCallback(() => {
    onClose();
    if (hasSavedSuggestionRef.current) {
      refetchDataAfterClose();
    }
  }, [onClose, refetchDataAfterClose]);

  const handleComplete = useCallback(() => {
    onClose();
    refetchDataAfterClose();
  }, [onClose, refetchDataAfterClose]);

  const onSuggestionSaved = useCallback(
    (suggestionID: string) => {
      onSuggestionReviewed(suggestionID);
      savedSuggestionIDsRef.current.add(suggestionID);
      if (!isOpenRef.current) {
        refetchDataAfterClose();
        return { isFinalSuggestion: false };
      }
      hasSavedSuggestionRef.current = true;

      return {
        isFinalSuggestion:
          initialTotalCountRef.current !== null &&
          savedSuggestionIDsRef.current.size >= initialTotalCountRef.current,
      };
    },
    [onSuggestionReviewed, refetchDataAfterClose]
  );

  useEffect(() => {
    if (!props.isOpen) {
      hasSavedSuggestionRef.current = false;
      savedSuggestionIDsRef.current.clear();
      initialTotalCountRef.current = null;
    }
  }, [props.isOpen]);

  useEffect(() => {
    return () => {
      isOpenRef.current = false;
    };
  }, []);

  const {
    data: currentData,
    previousData,
    networkStatus,
  } = useFetchAiOnboardingModalDataV2Query({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !props.isOpen,
    variables: {
      householdID: householdId,
      suggestionsWhere: effectiveSuggestionsWhere,
      first: pageSize,
      after,
    },
    onError: (error) => {
      reportError('error fetching data for AIOnboardingModal', error, {
        householdId,
      });
      createErrorFeedback(
        'Could not load data. Please refresh the page and try again.'
      );
    },
  });
  // External cache evictions (e.g. another surface refetching aiSuggestions
  // while this modal is open) make `data` momentarily undefined during the
  // resulting refetch. Fall back to the previous result so the open modal
  // never flashes an empty household or blank screen. A page change still
  // shows the loading state through its distinct network status.
  const data = currentData ?? previousData;

  // Review one scopes the paginated query to the single clicked suggestion,
  // so its page already is the whole submission scope.
  const skipAllSuggestionsQuery =
    !props.isOpen || Boolean(initialSelectedSuggestion);
  const {
    data: currentAllSuggestionsData,
    previousData: previousAllSuggestionsData,
    networkStatus: allSuggestionsNetworkStatus,
  } = useFetchAiOnboardingModalAllSuggestionsV2Query({
    notifyOnNetworkStatusChange: true,
    skip: skipAllSuggestionsQuery,
    variables: {
      householdID: householdId,
      suggestionsWhere: effectiveSuggestionsWhere,
    },
    onError: (error) => {
      reportError(
        'error fetching submission-scope suggestions for AIOnboardingModal',
        error,
        { householdId }
      );
      createErrorFeedback(
        'Could not load data. Please refresh the page and try again.'
      );
    },
  });
  const allSuggestionsData =
    currentAllSuggestionsData ??
    (allSuggestionsNetworkStatus === NetworkStatus.refetch
      ? previousAllSuggestionsData
      : undefined);

  const {
    household,
    suggestions,
  }: {
    household: AiOnboardingModal_HouseholdFragment;
    suggestions: AiOnboardingModal_AiSuggestionFragment[];
  } = useMemo(() => {
    const household = first(getNodes(data?.households));

    if (!data || !household) {
      return {
        household: {
          id: householdId,
          billableKind: HouseholdBillableKind.Billable,
          possiblePrimaryClients: [],
          possibleGrantors: [],
          isTwoGrantor: false,
          documents: [],
          entities: { edges: [] },
        },
        suggestions: [],
      };
    }

    return {
      household,
      suggestions: getNodes(data?.aiSuggestions),
    };
  }, [data, householdId]);

  // Confirm/Confirm all submits the whole query scope, never one page. Until
  // the scope query resolves we keep the modal in its loading state so the
  // user cannot submit a partial set.
  const suggestionsForSubmission = useMemo(() => {
    if (skipAllSuggestionsQuery) {
      return suggestions;
    }

    return getNodes(allSuggestionsData?.aiSuggestions);
  }, [skipAllSuggestionsQuery, suggestions, allSuggestionsData]);
  const allSuggestionsReady =
    skipAllSuggestionsQuery || Boolean(allSuggestionsData);
  const pageLoading = isAIOnboardingModalPageLoading(
    Boolean(currentData),
    Boolean(previousData),
    networkStatus
  );

  const selectedSuggestionWithEvidence = useMemo(() => {
    if (!initialSelectedSuggestion) return undefined;

    return suggestions.find(
      (suggestion) => suggestion.id === initialSelectedSuggestion.id
    );
  }, [initialSelectedSuggestion, suggestions]);

  // Review-all has no selected suggestion, so use the first fully fetched
  // suggestion only to choose the initial document.
  const initialSuggestionForDocument = initialSelectedSuggestion
    ? selectedSuggestionWithEvidence
    : first(suggestions);
  const initialDocumentId = getInitialDocumentIdFromSuggestion(
    initialSuggestionForDocument
  );

  const suggestionsConnection = data?.aiSuggestions;
  const hasNextPage = suggestionsConnection?.pageInfo.hasNextPage ?? false;
  if (
    props.isOpen &&
    !pageLoading &&
    suggestionsConnection &&
    initialTotalCountRef.current === null
  ) {
    // Keep the session baseline stable. Later page fetches can include saved
    // accept/merge actions in the server count, while local rejects are not
    // persisted until final submission.
    initialTotalCountRef.current = suggestionsConnection.totalCount;
  }
  const totalCount = Math.max(
    (initialTotalCountRef.current ?? suggestionsConnection?.totalCount ?? 0) -
      reviewedSuggestionIDs.length,
    0
  );
  const currentPageCount = suggestions.filter(
    (suggestion) =>
      !suggestion.acceptanceStatus &&
      !reviewedSuggestionIDs.includes(suggestion.id)
  ).length;

  const handleNextPage = useCallback(() => {
    if (!hasNextPage) {
      return;
    }

    onNextPage(suggestionsConnection?.pageInfo.endCursor);
  }, [hasNextPage, onNextPage, suggestionsConnection?.pageInfo.endCursor]);

  const suggestionsPagination = useMemo(
    () => ({
      currentPage,
      pageSize,
      totalCount,
      currentPageCount,
      reviewedCountBeforeCurrentPage,
      hasNextPage,
      hasPreviousPage,
      onNextPage: handleNextPage,
      onPreviousPage,
    }),
    [
      currentPage,
      pageSize,
      totalCount,
      currentPageCount,
      reviewedCountBeforeCurrentPage,
      hasNextPage,
      hasPreviousPage,
      handleNextPage,
      onPreviousPage,
    ]
  );

  return (
    <MultiDocumentUploaderProvider>
      <DocumentChatProvider documentId={initialDocumentId}>
        <AIOnboardingModalProvider
          key={currentPage}
          isOpen={props.isOpen}
          onClose={handleClose}
          onComplete={handleComplete}
          onSuggestionSaved={onSuggestionSaved}
          variant={variant}
          loading={pageLoading || !allSuggestionsReady}
          household={household}
          suggestions={suggestions}
          suggestionsForSubmission={suggestionsForSubmission}
          suggestionsPagination={suggestionsPagination}
          reviewedSuggestionIDs={reviewedSuggestionIDs}
          additionalReferenceSuggestions={additionalReferenceSuggestions}
          footerRef={footerRef}
          skipNavigationOnComplete={skipNavigationOnComplete}
        >
          <SelectedSuggestionEvidenceProvider
            initialSelectedSuggestion={selectedSuggestionWithEvidence}
          >
            <AIOnboardingModalInner initialDocumentId={initialDocumentId} />
          </SelectedSuggestionEvidenceProvider>
        </AIOnboardingModalProvider>
      </DocumentChatProvider>
    </MultiDocumentUploaderProvider>
  );
}
